'use client';

import { PricingPlan } from "@/app/types/PricingPlan";
import PricingTable from "@/components/billing/pricing-table";
import Intercom from "@intercom/messenger-js-sdk";
import { NextPage } from "next";
import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/navigation";
import { getCouponInfo, getPaygPricing, getPricing, type CouponInfo, type PaygPricing } from "@/services/billing";
import PercentageIcon from "@/assets/icons/percentage.svg";
import { Button } from "@/components/buttons/button";
import { analytics } from "@/app/analytics";
import { normalizePaygAmountCents } from "@/lib/payg-pricing";

const PricingTablePage: NextPage = () => {
    const searchParams = useSearchParams();
    const router = useRouter();
    const [discountApplied, setDiscountApplied] = useState(false);
    const [couponInfo, setCouponInfo] = useState<CouponInfo | null>(null);
    const [pricing, setPricing] = useState<{ planType: 'starter' | 'pro' | 'advanced', credits: number, price: number, originalPrice: number, interval: string, discountPercentage: number | null, hasDiscount?: boolean }[]>([]);
    const [paygPricing, setPaygPricing] = useState<PaygPricing | null>(null);
    const [paygPricingError, setPaygPricingError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
    const utmMedium = searchParams.get('utm_medium');
    const couponCode = searchParams.get('coupon_code');
    const initialPaygAmountCents = normalizePaygAmountCents(
        searchParams.get('amountCents'),
        paygPricing,
    ) ?? 0;
    const initialAutoRenew = searchParams.get('autoRenew') === 'true';
    
    useEffect(() => {
        Intercom({ region: 'us', app_id: 'qeeqze6n', hide_default_launcher: true });
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {                
                const pricingData = await getPricing(discountApplied ? (couponCode || utmMedium || '') : undefined);                
                setPricing(pricingData);
                try {
                    setPaygPricing(await getPaygPricing());
                    setPaygPricingError(false);
                } catch (error) {
                    console.error('Failed to fetch PAYG pricing:', error);
                    setPaygPricing(null);
                    setPaygPricingError(true);
                }

                if (couponCode || utmMedium) {
                    const info = await getCouponInfo(couponCode || utmMedium || '');                    
                    setCouponInfo(info);                    
                }
            } catch (error) {
                console.error('Failed to fetch data:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [utmMedium, couponCode, discountApplied]);

    const handleApplyDiscount = async () => {
        setIsApplyingDiscount(true);
        try {
            analytics.getTrackerInstance().discountApplied({
                utmMedium: utmMedium || '',
            });
            setDiscountApplied(true);
        } finally {
            setIsApplyingDiscount(false);
        }
    };

    const changePlan = async (plan: PricingPlan) => {
        const params = new URLSearchParams();
        params.set('planId', plan.planId);
        params.set('planType', plan.planType);
        params.set('isYearly', plan.isYearly.toString());
        const selectedPricing = pricing.find(p => p.planType === plan.planType && p.interval === (plan.isYearly ? 'year' : 'month'));
        if (discountApplied && couponInfo?.id && selectedPricing?.hasDiscount) {
            params.set('stripeCouponId', couponInfo.id);
        }

        if (couponCode) {
            params.set('coupon_code', couponCode);
        }

        const url = `https://app.clearitty.com/earn-credits/billing/checkout?${params.toString()}`;
        window.open(url);
    };

    const handlePaygCheckout = (amountCents: number, autoRenew: boolean) => {
        analytics.getTrackerInstance().track?.({
            event_type: 'PAYG Checkout Started',
            event_properties: { amountCents, autoRenew, publicMode: true },
        } as any);
        const params = new URLSearchParams();
        params.set('billingMode', 'payg');
        params.set('amountCents', String(amountCents));
        params.set('autoRenew', String(autoRenew));
        const redirectUrl = `/earn-credits/billing?${params.toString()}`;
        window.open(`https://app.clearitty.com/signup?redirect_to=${encodeURIComponent(redirectUrl)}`, '_blank');
    };

    const handleModeChange = (mode: 'annual' | 'monthly' | 'payg') => {
        const params = new URLSearchParams(searchParams);
        params.set('billingMode', mode);
        router.replace(`/earn-credits/billing/pricing-table?${params.toString()}`, { scroll: false });
    };

    return (
        <div className="flex flex-col items-center justify-center bg-gray-100">
            {couponInfo?.id && couponInfo.valid && (
                <div className="flex w-full items-center justify-center p-6 md:p-0">
                    <div className="bg-[#fff9e5] rounded shadow-[4px_4px_0px_0px_rgba(0,0,0,1.00)] border-2 border-black p-6 mb-6 flex flex-col md:flex-row items-center justify-center gap-4 md:justify-between">
                        <div className="flex md:hidden w-full items-start">
                            <PercentageIcon />
                        </div>

                        <div className="text-gray-900">
                            <div className="flex items-center gap-4">
                                <PercentageIcon className="hidden md:block" />

                                <div className="flex flex-col">
                                    <span className="text-black text-base md:text-lg font-medium">{couponInfo?.metadata?.title}</span>
                                    <span className="text-black text-xl md:text-2xl font-semibold">
                                        Use {couponInfo?.name || utmMedium} for {couponInfo?.percentOff}% discount today!
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex w-full md:w-auto justify-end md:justify-center items-center">
                            <Button
                                onClick={handleApplyDiscount}
                                className="bg-indigo-600 text-white hover:bg-indigo-700 ml-4 rounded"
                                disabled={discountApplied || isApplyingDiscount}
                            >
                                {discountApplied ? 'Discount applied!' : isApplyingDiscount ? 'Applying...' : 'Apply Discount'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            <PricingTable
                pricing={pricing}
                paygPricing={paygPricing}
                paygPricingError={paygPricingError}
                showPayg
                publicMode={true}
                changePlan={changePlan}
                onPaygCheckout={handlePaygCheckout}
                onModeChange={handleModeChange}
                initialMode={searchParams.get('billingMode') === 'annual'
                    ? 'annual'
                    : searchParams.get('billingMode') === 'monthly'
                        ? 'monthly'
                        : 'payg'}
                initialPaygAmountCents={initialPaygAmountCents}
                initialAutoRenew={initialAutoRenew}
                discountApplied={discountApplied}
                isLoading={isLoading}                
            />
        </div>
    );
};

export default PricingTablePage;
